import { useNavigate } from 'react-router-dom';
import { Banknote, Coins } from 'lucide-react';
import {
  DeltaCell,
  MetricCard,
  NumberedPagination,
  SubUnitStatsTable,
  formatUsd,
  type SubUnitColumn,
  MONEY,
  accountStatus,
} from '@kioskgaming/ui';
import type { AgentDashboardShopsData, DashboardV2Period } from '../../services/api';

type ShopRow = AgentDashboardShopsData['shops'][number];

type Props = {
  period: DashboardV2Period;
  data: AgentDashboardShopsData | null;
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
};

const columns: Array<SubUnitColumn<ShopRow>> = [
  {
    key: 'name',
    header: 'Shop',
    render: (row) => (
      <span className="font-medium text-gray-900">
        {row.name}
        {row.status !== 'active' ? (
          <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
            {accountStatus(row.status).label}
          </span>
        ) : null}
      </span>
    ),
  },
  {
    key: 'creditsPurchased',
    header: MONEY.creditsSold,
    tooltip: 'USD this Shop paid you for credits in the period (cash, online purchase or balance convert). Their retail activity is not included.',
    align: 'right',
    render: (row) => <DeltaCell kpi={row.creditsPurchased} format={formatUsd} />,
  },
  {
    key: 'cash',
    header: 'Cash expected / received',
    align: 'right',
    render: (row) => (
      <span className="tabular-nums">
        {formatUsd(row.cashExpected)} <span className="text-gray-400">/</span> {formatUsd(row.cashReceived)}
      </span>
    ),
  },
  {
    key: 'networkDeposits',
    header: 'Player deposits',
    align: 'right',
    render: (row) => formatUsd(row.networkDeposits),
  },
  {
    key: 'networkWithdrawals',
    header: 'Player withdrawals',
    align: 'right',
    render: (row) => formatUsd(row.networkWithdrawals),
  },
];

export default function ShopsTab({ data, isLoading, page, onPageChange }: Props) {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricCard
          label={`${MONEY.creditsSold} (all Shops)`}
          tooltip="Total USD your Shops paid you for credits in the period."
          value={formatUsd(data?.summary.creditsPurchased ?? 0)}
          tone="amber"
          icon={Coins}
          loading={isLoading}
        />
        <MetricCard
          label="Cash expected"
          tooltip="Cash owed to you on cash credit sales to Shops in the period."
          value={formatUsd(data?.summary.cashExpected ?? 0)}
          tone="emerald"
          icon={Banknote}
          loading={isLoading}
        />
        <MetricCard
          label="Cash received"
          tooltip="Cash actually collected on credit sales to Shops in the period."
          value={formatUsd(data?.summary.cashReceived ?? 0)}
          tone="emerald"
          icon={Banknote}
          loading={isLoading}
        />
      </div>

      <SubUnitStatsTable
        columns={columns}
        rows={data?.shops ?? []}
        rowKey={(row) => row.id}
        loading={isLoading}
        emptyLabel="No Shops in your network"
        onRowClick={(row) => navigate(`/shops/${row.id}`)}
      />

      {data && data.pagination.totalPages > 1 ? (
        <NumberedPagination
          currentPage={page}
          totalPages={data.pagination.totalPages}
          onPageChange={onPageChange}
          variant="portal"
        />
      ) : null}
    </div>
  );
}
